import { z } from 'zod'
import {
  AnalyticsRoutes,
  BroadcastsRoutes,
  ChatbotRoutes,
  ContactsRoutes,
  ConversationRoutes,
  DiscoverRoutes,
  ChatbotPath,
  ConversationsPath,
  CompliancePath,
  NumbersPath,
  RcsPath,
  InboxesPath,
  SurveysRoutes,
} from 'entities/ExportRouters'

/**
 * Main app areas the agent can navigate to, matching the sidebar
 * (widgets/Sidebar/constants/menuItems.ts → menuItemsV2). Labels are the
 * human-facing sidebar names so the model can reason about them naturally.
 *
 * Route roots come from entities/ExportRouters — the same source menuItemsV2
 * uses — rather than importing menuItemsV2 itself (that lives in widgets/, and
 * entities/ importing widgets/ would violate FSD layering).
 */
export const NAV_AREAS = [
  'Inboxes',
  'Discover',
  'AI & Automation',
  'Outbound',
  'Contacts',
  'Analytics',
] as const

export type NavArea = (typeof NAV_AREAS)[number]

// All URL-hydratable, so the agent deep-links straight to them. New Contact and
// Import gained routes in SWR-24198 / SWR-24199 rather than bespoke tools.
export const NAV_DESTINATIONS = [
  'Calls',
  'Compliance',
  'Business Profile',
  '10DLC Registration',
  'Toll-Free Registration',
  'RCS',
  'Textbot',
  'Number Provisioning',
  'Inbox Settings',
  'New Contact',
  'Import',
  'Surveys',
] as const

export type NavDestination = (typeof NAV_DESTINATIONS)[number]

export const NAV_TARGETS = [...NAV_AREAS, ...NAV_DESTINATIONS] as const

export type NavTarget = (typeof NAV_TARGETS)[number]

export const navigateSchema = z.object({
  area: z.enum(NAV_TARGETS).describe('App area or deeper screen to navigate to'),
})

/**
 * What a destination is for, where its name alone is ambiguous or the screen is
 * hard to find. Only the targets that need it appear here.
 */
const NAV_TARGET_HINTS: Partial<Record<NavTarget, string>> = {
  Inboxes: 'the conversations screen',
  Outbound: 'broadcasts and campaigns',
  'AI & Automation': 'AI agents and automation',
  Compliance:
    'Settings → Compliance, the overview of every registration. Use this when the user has not answered the compliance questionnaire yet, because it lands there',
  'Business Profile':
    'the Business Profile registration, the first compliance step everything else depends on',
  '10DLC Registration':
    'Settings → Compliance → Local numbers, where the Brand and the Campaign are registered. Use this for either of them',
  'Toll-Free Registration': 'Settings → Compliance → Toll-free, for verifying toll-free numbers',
  RCS: 'Settings → RCS, for the RCS sender profile',
  Textbot: 'the AI Textbot setup',
  'Number Provisioning': 'getting a phone number',
  'Inbox Settings':
    'Settings → Inboxes, for configuring an inbox, including enabling voice and calling. Use this, NOT Inboxes, for anything about inbox configuration',
  'New Contact': 'the new-contact form',
  Import: 'the contact import flow',
  Surveys: 'collecting feedback from contacts over text',
}

const describeTarget = (target: NavTarget): string => {
  const hint = NAV_TARGET_HINTS[target]
  return hint ? `${target} (${hint})` : target
}

/**
 * The `navigate` tool description, generated from NAV_TARGETS so it cannot drift
 * from the schema the way a hand-written list did (SWC-19707). The per-target
 * hints live here rather than in the system prompt for the same reason.
 *
 * One destination per line, not a semicolon-joined run. As a run, the list was a
 * single 738-character sentence and Surveys was the fourteenth of fourteen
 * entries, 93% of the way through, directly after the longest hint (which carries
 * an emphatic instruction of its own and reads like the end of the content). The
 * agent declined to open Surveys on the grounds that it was "not available through
 * my navigation tools". Anything appended here lands in that tail, so keep the
 * list scannable.
 */
export const NAVIGATE_DESCRIPTION = `Navigate the app to a main area or a deeper screen. Call this when the user asks to go to, open, or show one of these. Check every line of the list before telling the user you cannot reach a screen.

area is one of:
${NAV_TARGETS.map((target) => `- ${describeTarget(target)}`).join('\n')}`

export const NAV_AREA_ROUTES: Record<NavArea, string> = {
  Inboxes: ConversationRoutes.root,
  Discover: DiscoverRoutes.root,
  'AI & Automation': ChatbotRoutes.root,
  Outbound: BroadcastsRoutes.root,
  Contacts: ContactsRoutes.root,
  Analytics: AnalyticsRoutes.root,
}

/**
 * Deep-destination routes, sourced from the page route builders (via
 * entities/ExportRouters). Some builders include a leading slash and some do
 * not; resolveNavPath normalises that.
 */
export const NAV_DESTINATION_ROUTES: Record<NavDestination, string> = {
  Calls: ConversationsPath.calls,
  Compliance: CompliancePath.compliance.root,
  'Business Profile': CompliancePath.compliance.businessProfile.root,
  '10DLC Registration': CompliancePath.compliance.localNumbers.root,
  'Toll-Free Registration': CompliancePath.compliance.tollFree.root,
  RCS: RcsPath.root,
  Textbot: ChatbotPath.texting,
  'Number Provisioning': NumbersPath.root,
  'Inbox Settings': InboxesPath.root,
  'New Contact': `${ContactsRoutes.root}/${ContactsRoutes.new}`,
  Import: `${ContactsRoutes.root}/${ContactsRoutes.import}`,
  Surveys: SurveysRoutes.root,
}

export const resolveNavPath = (target: NavTarget): string => {
  const raw = Object.prototype.hasOwnProperty.call(NAV_AREA_ROUTES, target)
    ? NAV_AREA_ROUTES[target as NavArea]
    : NAV_DESTINATION_ROUTES[target as NavDestination]
  return `/${raw.replace(/^\/+/, '')}`
}
